(function () {
  const SELECTORS = {
    root: '[data-predictive-search]',
    input: '[data-predictive-search-input]',
    results: '[data-predictive-search-results]',
  };

  function escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function renderNoResults(term) {
    const template = window.theme?.i18n?.noResultsFor || 'No results for “{{ term }}”.';
    return template.replace('{{ term }}', escapeHtml(term));
  }

  function renderError() {
    return window.theme?.i18n?.error || 'Something went wrong.';
  }

  function renderLoading() {
    const label = window.theme?.i18n?.loading || 'Searching…';
    return `
      <div class="flex w-full p-10 h-full rounded-md items-center justify-center gap-3 text-sm font-medium">
        <svg class="animate-spin w-[35px] h-[35px]" width="35" height="35" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56.7 48.2" aria-hidden="true">
          <path d="M40.6,19.8 54.3,20.4c4.6,.82,1.5,12.35,0,15.41-3.41,6.87-9.81,10.4-17.46,10.36-3.07-.02-11.95-1.54-10.64-6.01,1.10-3.76,5.07-.85,6.76-.47,9.6,2.19,17.42-3.34,17.55-13.27-.95-1.48-11.94.39-14.19-1.02-1.44-.91-1.65-5.27-2.25-7.22-2.22-7.25-8.73-14.06-16.93-11.47-9.51,3.01-12.35,15.9-10.76,24.65.35,1.93,3.43,10.75,5.51,10.75,2.94,0,5.19-11.56,5.25-14.03.07-3.10-1.84-8.81-1.51-10.90.50-3.12,4.67-3.18,6.02,.44,2.89,7.74,1.33,30.35-9.78,30.66-4.84.13-8.59-7.07-9.94-10.99C-1.55,27.14-.47,15.72,6.39,7.37c12.71-15.47,31.24-4.30,34.24,12.40Z" fill="#6a100d"/>
        </svg>
      </div>
    `;
  }

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  async function fetchSuggestions(term, limit = 6) {
    const q = (term || '').trim();
    if (!q) return null;

    const params = new URLSearchParams();
    params.set('q', q);
    params.set('resources[type]', 'product,collection,page,article');
    params.set('resources[limit]', String(limit));
    params.set('options[prefix]', 'last');

    const base = `${window.Shopify?.routes?.root || '/'}`;
    const url = `${base}search/suggest.json?${params.toString()}`;

    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (res.status === 422) return null;
    if (!res.ok) throw new Error(`Suggest request failed: ${res.status}`);
    return res.json();
  }

  function renderResults(json, term) {
    const resources = json?.resources?.results || {};
    const products = resources.products || [];
    const collections = resources.collections || [];
    const pages = resources.pages || [];
    const articles = resources.articles || [];
    const hasAny = products.length || collections.length || pages.length || articles.length;

    if (!hasAny) {
      return `<div class="text-sm opacity-70">${renderNoResults(term)}</div>`;
    }

    const renderList = (title, items, mapItem) => {
      if (!items.length) return '';
      return `
        <div class="mb-7">
          <div class="text-xs font-semibold opacity-70 mb-4">${escapeHtml(title)}</div>
          <ul class="text-sm font-medium space-y-4">
            ${items.map(mapItem).join('')}
          </ul>
        </div>
      `;
    };

    function formatPriceRaw(val) {
      if (val === null || val === undefined || val === '') return '';
      const n = Number(val);
      if (Number.isNaN(n)) return escapeHtml(String(val));

      let major = n;
      if (!String(val).includes('.') && Math.abs(n) >= 1000) major = n / 100;

      const hasFraction = Math.abs(major % 1) > 0;
      const formatted = major.toLocaleString(undefined, {
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2,
      });

      const currency = window.Shopify?.currency?.active || 'SEK';
      return `${escapeHtml(formatted)} ${escapeHtml(currency)}`;
    }

    const productItem = (p) => {
      const rawPrice = p.price ?? p.price_min ?? p.price_amount ?? null;
      const rawCompare = p.compare_at_price_min ?? p.compare_at_price ?? p.compare_at_price_max ?? null;

      const priceValue = rawPrice !== null ? Number(rawPrice) : NaN;
      const compareValue = rawCompare !== null ? Number(rawCompare) : NaN;

      const tagsArray = Array.isArray(p.tags)
        ? p.tags
        : typeof p.tags === 'string' && p.tags.length
          ? p.tags.split(',')
          : [];
      const tagsLower = tagsArray.map((t) => String(t).trim().toLowerCase()).join(',');

      let priceHtml = '';
      if (!Number.isNaN(compareValue) && !Number.isNaN(priceValue) && compareValue > priceValue) {
        priceHtml = `
          <span class="text-xs font-medium">
            <ins class="text-accent no-underline">${formatPriceRaw(priceValue)}</ins>
            <del class="ms-1 text-black opacity-50">${formatPriceRaw(compareValue)}</del>
          </span>
        `;
      } else if (!Number.isNaN(priceValue)) {
        priceHtml = `<span class="text-xs font-medium">${formatPriceRaw(priceValue)}</span>`;
      } else {
        priceHtml = `<span class="text-xs font-medium">${escapeHtml(String(p.price || ''))}</span>`;
      }

      let badgesHtml = '';
      if (tagsLower.includes('new')) {
        badgesHtml += `<span class="theme-dark font-semibold h-[24px] items-center flex px-2 py-[2px] text-[10px] rounded new">${window.theme?.i18n?.new}</span>`;
      }
      if (!Number.isNaN(compareValue) && !Number.isNaN(priceValue) && compareValue > priceValue) {
        const savings = compareValue - priceValue;
        const percent = Math.round((savings * 100) / compareValue);
        badgesHtml += `<span class="theme-dark font-semibold h-[24px] items-center flex px-2 py-[2px] text-[10px] rounded sale">${window.theme?.i18n?.save} ${escapeHtml(String(percent))}%</span>`;
      }

      return `
        <li class="last-of-type:border-b-0 border-b border-light pb-4">
          <a class="flex gap-4 relative" href="${escapeHtml(p.url)}">
            ${p.image ? `<div class="w-[85px] h-[85px] p-2 rounded-lg theme-light flex items-center justify-center overflow-hidden">
              <img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" class="w-full h-full object-contain" loading="lazy">
            </div>` : ''}

            <div class="flex flex-1 flex-col">
              <div>
                <p class="text-sm font-semibold">${escapeHtml(p.title)}</p>
                ${priceHtml}
              </div>

              <div class="mt-auto flex flex-wrap gap-1">
                ${badgesHtml}
              </div>
            </div>
          </a>
        </li>
      `;
    };

    const simpleItem = (x) => `
      <li>
        <a class="text-sm hover:opacity-80" href="${escapeHtml(x.url)}">
          ${escapeHtml(x.title)}
        </a>
      </li>
    `;

    return `
      ${renderList('Products', products, productItem)}
      ${renderList('Collections', collections, simpleItem)}
      ${renderList('Pages', pages, simpleItem)}
      ${renderList('Articles', articles, simpleItem)}
    `;
  }

  function setState(root, state) {
    const drawer = root.closest('.drawer') || root;
    drawer.classList.remove('is-loading', 'has-results', 'has-no-results', 'has-error', 'has-query');
    if (state) drawer.classList.add(state);
    if (state !== null) drawer.classList.add('has-query');
  }

  function setup(root) {
    const input = root.querySelector(SELECTORS.input);
    const results = root.querySelector(SELECTORS.results);
    const viewBtn = root.querySelector('[data-view-results]');
    if (!input || !results) return;

    let lastTerm = '';
    let abortController = null;

    const closeResults = () => {
      results.classList.add('hidden');
      results.innerHTML = '';
      results.removeAttribute('role');
      const term = input.value.trim();
      if (!term) setState(root, null);
    };

    function updateViewButton(term, hasResults) {
      if (!viewBtn) return;

      if (term && hasResults) {
        viewBtn.classList.remove('opacity-50', 'pointer-events-none');
        viewBtn.setAttribute('aria-disabled', 'false');
        viewBtn.href = `/search?q=${encodeURIComponent(term)}`;
      } else {
        viewBtn.classList.add('opacity-50', 'pointer-events-none');
        viewBtn.setAttribute('aria-disabled', 'true');
        viewBtn.href = '#';
      }
    }

    const openResults = (html) => {
      results.innerHTML = html;
      results.classList.remove('hidden');
      results.setAttribute('role', 'listbox');
    };

    const run = debounce(async () => {
      const term = input.value.trim();

      if (!term) {
        lastTerm = '';
        closeResults();
        setState(root, null);
        updateViewButton('', false);
        return;
      }

      if (term.length < 2) {
        setState(root, 'has-query');
        closeResults();
        return;
      }

      if (term === lastTerm) return;

      lastTerm = term;

      if (abortController) abortController.abort();
      abortController = new AbortController();

      setState(root, 'is-loading');
      updateViewButton(term, false);
      openResults(renderLoading());

      try {
        const json = await fetchSuggestions(term);

        if (!json) {
          setState(root, 'has-no-results');
          openResults(`<div class="p-3 text-sm opacity-70">${renderNoResults(term)}</div>`);
          return;
        }

        const html = renderResults(json, term);
        openResults(html);

        const resources = json?.resources?.results || {};
        const hasAny =
          (resources.products?.length || 0) ||
          (resources.collections?.length || 0) ||
          (resources.pages?.length || 0) ||
          (resources.articles?.length || 0);

        setState(root, hasAny ? 'has-results' : 'has-no-results');
        updateViewButton(term, !!hasAny);
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.error(e);
        setState(root, 'has-error');
        updateViewButton(term, false);
        openResults(`<div class="p-3 text-sm text-red-600">${renderError()}</div>`);
      }
    }, 150);

    input.addEventListener('input', run);

    document.addEventListener('click', (e) => {
      if (!root.contains(e.target)) closeResults();
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeResults();
        input.blur();
      }
    });

    input.addEventListener('focus', () => {
      if (results.innerHTML.trim()) results.classList.remove('hidden');
    });
  }

  function initAll() {
    document.querySelectorAll(SELECTORS.root).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();